import React, { useState } from "react";
import { Select, Input, Button, DatePicker } from "antd";
import dayjs, { Dayjs } from "dayjs";
import { REFERRAL_STATUSES } from "../../constants/recruitment.constants";

const { RangePicker } = DatePicker;

const REFERRAL_STATUS_OPTS = REFERRAL_STATUSES.map((s) => ({
  value: s,
  label: s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
}));

export interface ReferralFiltersState {
  search: string;
  status: string;
  date_from?: string;
  date_to?: string;
}

interface ReferralFiltersProps {
  filters: ReferralFiltersState;
  onFilterChange: (partial: Partial<ReferralFiltersState>) => void;
  loading: boolean;
  onReferClick: () => void;
}

const ReferralFilters: React.FC<ReferralFiltersProps> = ({
  filters,
  onFilterChange,
  loading,
  onReferClick,
}) => {
  const [localSearch, setLocalSearch] = useState(filters.search || "");
  const [localDateRange, setLocalDateRange] = useState<[Dayjs, Dayjs] | null>(
    filters.date_from && filters.date_to
      ? [dayjs(filters.date_from), dayjs(filters.date_to)]
      : null,
  );

  const getActiveFiltersCount = () => {
    let count = 0;
    if (filters.status) count++;
    if (filters.search) count++;
    if (filters.date_from || filters.date_to) count++;
    return count;
  };
  const activeFiltersCount = getActiveFiltersCount();

  const handleReset = () => {
    setLocalSearch("");
    setLocalDateRange(null);
    onFilterChange({ search: "", status: "", date_from: undefined, date_to: undefined });
  };

  const handleSearchClick = () => {
    onFilterChange({ search: localSearch });
  };

  const handleDateRangeChange = (dates: [Dayjs | null, Dayjs | null] | null) => {
    if (dates && dates[0] && dates[1]) {
      setLocalDateRange([dates[0], dates[1]]);
      onFilterChange({
        date_from: dates[0].format("YYYY-MM-DD"),
        date_to: dates[1].format("YYYY-MM-DD"),
      });
    } else {
      setLocalDateRange(null);
      onFilterChange({ date_from: undefined, date_to: undefined });
    }
  };

  return (
    <div className="card">
      
      <div className="card-header border-0 pb-0">
        <div className="d-flex align-items-center justify-content-between mb-3">
          <div>
            <h4 className="mb-1 d-flex align-items-center">
              <i className="ti ti-user-plus text-primary fs-32 me-2" />
              Referral Tracking
            </h4>
            <p className="text-muted mb-0">
              Track all candidates referred by your team
            </p>
          </div>
          <div className="d-flex align-items-center gap-2">
            <button
              className="btn btn-info btn-md d-flex align-items-center gap-1"
              onClick={onReferClick}
            >
              <i className="ti ti-user-plus" />
              Refer a Candidate
            </button>
          </div>
        </div>
      </div>

      
      <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
        
        <div className="d-flex gap-2">
          <div className="d-flex gap-2 mb-2 me-2">
            <Input
              placeholder="Search candidate, referrer, job..."
              value={localSearch}
              onChange={(e) => {
                const value = e.target.value;
                setLocalSearch(value);
                if (value === "") onFilterChange({ search: "" });
              }}
              onPressEnter={handleSearchClick}
              style={{ width: 250, minHeight: 38 }}
              allowClear
            />
            <Button type="primary" onClick={handleSearchClick} style={{ minHeight: 38 }}>
              <i className="ti ti-search me-1" />
            </Button>
          </div>
        </div>

        
        <div className="d-flex my-xl-auto right-content align-items-center flex-wrap row-gap-3">
          <div className="me-2 mb-2">
            <Select
              style={{ width: 180, minHeight: 38 }}
              placeholder="All Statuses"
              value={filters.status || undefined}
              allowClear
              options={REFERRAL_STATUS_OPTS}
              onChange={(val) => onFilterChange({ status: val || "" })}
            />
          </div>
          <div className="me-2">
            <RangePicker
              size="small"
              value={localDateRange}
              onChange={(dates) =>
                handleDateRangeChange(dates as [Dayjs | null, Dayjs | null] | null)
              }
              allowClear
              placeholder={["Referred From", "Referred To"]}
              style={{ borderRadius: 8 }}
              format="DD MMM YYYY"
            />
          </div>
          <div>
            <button
              type="button"
              className={`btn btn-md ${activeFiltersCount > 0 ? "btn-warning" : "btn-outline-danger"} mb-2 position-relative py-2`}
              onClick={handleReset}
            >
              <i className="ti ti-refresh me-1" />
              Reset
              {activeFiltersCount > 0 && (
                <span
                  className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger"
                  style={{ fontSize: "10px" }}
                >
                  {activeFiltersCount}
                </span>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReferralFilters;
